/**
 * S3 Utilities for handling presigned URLs and upload headers
 */

/**
 * Parse presigned URL to extract required headers and metadata
 * @param {string} presignedUrl - The S3 presigned URL
 * @returns {Object} Object containing headers, contentType, and other metadata
 */
export function parsePresignedUrl(presignedUrl) {
  try {
    const url = new URL(presignedUrl);
    const params = new URLSearchParams(url.search);
    
    // Extract Content-Type from URL parameters
    const contentType = params.get('Content-Type') || 'application/octet-stream';
    
    // Extract all x-amz-meta-* headers
    const metaHeaders = {};
    for (const [key, value] of params.entries()) {
      if (key.startsWith('x-amz-meta-')) {
        metaHeaders[key] = value;
      }
    }
    
    // Extract security token if present
    const securityToken = params.get('x-amz-security-token');
    
    return {
      contentType,
      metaHeaders,
      securityToken,
      expires: params.get('Expires'),
      signature: params.get('Signature'),
      accessKeyId: params.get('AWSAccessKeyId')
    };
  } catch (error) {
    console.error('Error parsing presigned URL:', error);
    throw new Error('Invalid presigned URL format');
  }
}

/**
 * Create headers object for S3 upload from presigned URL
 * @param {string} presignedUrl - The S3 presigned URL
 * @returns {Object} Headers object for axios request
 */
export function createS3UploadHeaders(presignedUrl) {
  const parsed = parsePresignedUrl(presignedUrl);
  
  const headers = {
    'Content-Type': parsed.contentType,
    ...parsed.metaHeaders
  };
  
  // Add security token if present
  if (parsed.securityToken) {
    headers['x-amz-security-token'] = parsed.securityToken;
  }
  
  return headers;
}

/**
 * Create axios config for S3 upload with proper header handling
 * @param {string} presignedUrl - The S3 presigned URL
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Object} Axios config object
 */
export function createS3UploadConfig(presignedUrl, file, onProgress) {
  const uploadHeaders = createS3UploadHeaders(presignedUrl);
  
  return {
    headers: uploadHeaders,
    transformRequest: (data, headers) => {
      // Remove any auth headers that might interfere with S3 signature
      delete headers['Authorization'];
      delete headers['authorization'];
      return data;
    },
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      onProgress(percentCompleted);
    } : undefined,
    // Skip auth interceptor to prevent header modification
    skipAuthInterceptor: true,
    // An S3 403 (expired or mismatched URL) is not an app permission problem;
    // without this the global interceptor would redirect the user away.
    skipPermissionRedirect: true
  };
}

/**
 * Validate presigned URL before upload
 * @param {string} presignedUrl - The S3 presigned URL
 * @returns {boolean} True if URL is valid and not expired
 */
export function validatePresignedUrl(presignedUrl) {
  try {
    const parsed = parsePresignedUrl(presignedUrl);
    
    // Check if URL has expired
    if (parsed.expires) {
      const expirationTime = parseInt(parsed.expires) * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      if (currentTime >= expirationTime) {
        console.warn('Presigned URL has expired');
        return false;
      }
    }
    
    // Check for required parameters
    if (!parsed.signature || !parsed.accessKeyId) {
      console.warn('Presigned URL missing required signature or access key');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Invalid presigned URL:', error);
    return false;
  }
}